// ui/src/components/TopNavBar.tsx

import React, { useEffect, useState } from "react"; // Import useEffect
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
// Import Home icon along with ArrowLeft
import { ArrowLeft, Home, CalendarIcon, Search as SearchIcon, XCircle, UserCog, X } from "lucide-react";
// Import firebaseAuth for logout
import { useAuth } from "utils/AuthContext"; // Use centralized auth
import { firebaseAuth, APP_BASE_PATH } from "app";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"; // Import Avatar components
import { useProfileStore } from "utils/useProfileStore";
import NotificationIcons from "components/NotificationIcons";
import NotificationBell from 'components/NotificationBell';
import { ThemeToggleMenu } from 'utils/ThemeToggle';
import { useWorkingHoursStore } from "utils/workingHoursStore";
import { useImpersonationStore } from "utils/useImpersonationStore";
import ActAsUserDialog from "components/ActAsUserDialog";


// Define paths that should NOT have navigation controls
const PUBLIC_PATHS = ["/login", "/signup", "/privacy"];

export function TopNavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, loading } = useAuth(); // Use centralized auth
  const { profile, isLoading: profileLoading } = useProfileStore();
  const { clearAllWorkingHours } = useWorkingHoursStore();
  const { impersonatedUser, isImpersonating, clearImpersonation } = useImpersonationStore();
  const [actAsUserDialogOpen, setActAsUserDialogOpen] = useState(false);

  const userId = user?.uid; // MYA-459: Use stable userId for dependency

  // Note: Profile subscription is handled by AppProvider.tsx
  // We only read from the store here to avoid duplicate Firestore listeners

  // Determine if the back button should be shown (show for all logged-in users when not on home page)
  const showBackButton = location.pathname !== "/" && !loading && user;

  // --- Logic to determine if user is on public path ---
  const basePathPrefix = APP_BASE_PATH === "/" || APP_BASE_PATH === "" ? "" : APP_BASE_PATH;
  let normalizedPathname = location.pathname;
  if (basePathPrefix && (normalizedPathname as string).startsWith(basePathPrefix)) {
    normalizedPathname = (normalizedPathname as string).substring((basePathPrefix as string).length);
  }
  normalizedPathname = (normalizedPathname.startsWith("/") ? normalizedPathname : `/${normalizedPathname}`).toLowerCase();
  const isPublicPath = PUBLIC_PATHS.includes(normalizedPathname);
  // showLoggedInControls is true if user logged in and not on public path
  const showLoggedInControls = !loading && user && !isPublicPath;

  const handleBack = () => {
    const currentPath = location.pathname;

    if (currentPath.startsWith("/EditClient")) {
      // Replace /EditClient in history with /ManageClients
      navigate("/manage-clients", { replace: true });
    } else if (currentPath.startsWith("/EditStaff")) {
      // Replace /EditStaff in history with /ManageStaff
      navigate("/ManageStaff", { replace: true });
    } else if (currentPath.startsWith("/EditAppointment")) {
      // Replace /EditAppointment in history with /AllAppointmentsAdmin
      navigate("/AllAppointmentsAdmin", { replace: true });      
    } else {
      // Default browser back behavior for all other pages
      navigate(-1);
    }
  };

  const handleHome = () => {
    // Change navigation target to the root path '/'
    navigate('/');
  };

  const handleLogin = () => {
    // Navigate directly to the correct lowercase route
    navigate('/login');
  };

  const handleLogout = async () => {
    try {
      // Clear all working hours listeners before signing out
      clearAllWorkingHours();
      
      await firebaseAuth.signOut();
      // Navigate to home page after successful logout
      navigate('/');
    } catch (error) {
      // Optionally show a toast notification on error
    }
  };

  return (
    <div className="bg-[#008080] text-white border-b border-gray-700 sticky top-0 z-50 h-12 flex items-center px-4 gap-2">
      {/* Back Button - Show for all logged-in users when not on home page */}
      {showBackButton ? (
        <Button variant="ghost" size="icon" onClick={handleBack} aria-label="Go back" className="text-white hover:bg-[#4A4C7D]">
          <ArrowLeft className="h-6 w-6" />
        </Button>
      ) : (
        // Placeholder to maintain spacing
        <div className="w-10 h-10"></div>
      )}

      {/* Home Button - Show if logged in AND not on the root path */}
      {showLoggedInControls && location.pathname !== '/' && (
          <Button variant="ghost" size="icon" onClick={handleHome} aria-label="Go to Dashboard" className="text-white hover:bg-[#4A4C7D]">
              <Home className="h-6 w-6" />
          </Button>
      )}

      {/* Search Input - Admin Only, after Home button */}
      {showLoggedInControls && profile && (profile.role === "admin" || profile.isAdmin || profile.isSuperAdmin) && (
        <div className="relative flex items-center">
          <SearchIcon className="absolute left-2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            className="pl-8 pr-3 py-1.5 w-32 sm:w-48 rounded-md bg-white/10 border border-white/20 text-white placeholder-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/20"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                navigate(`/search?q=${encodeURIComponent(e.currentTarget.value.trim())}`);
                e.currentTarget.value = '';
              }
            }}
          />
        </div>
      )}

      {/* Act as User Button - Super Admin Only */}
      {showLoggedInControls && profile?.isSuperAdmin && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setActAsUserDialogOpen(true)}
          className="text-white hover:bg-[#4A4C7D] gap-1"
        >
          <UserCog className="h-4 w-4" />
          <span className="hidden sm:inline">Act as User</span>
        </Button>
      )}

      {/* Impersonation Banner */}
      {isImpersonating && impersonatedUser && (
        <div className="flex items-center gap-2 bg-amber-500 text-white px-3 py-1 rounded-full text-sm">
          <span className="font-medium">
            Acting as: {impersonatedUser.firstName} {impersonatedUser.lastName}
          </span>
          <button
            onClick={() => {
              clearImpersonation();
              // Leaving a guardian portal impersonation — send the admin home.
              if (window.location.pathname.startsWith('/guardian')) {
                window.location.href = '/';
              } else {
                window.location.reload();
              }
            }}
            className="hover:bg-amber-600 rounded-full p-0.5"
            title="Stop impersonating"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* User Greeting and Avatar */}
      <div className="flex-grow flex items-center justify-end gap-2 sm:gap-3">
        {(loading || (user && profileLoading)) ? (
          // Skeleton loader while auth or profile is loading
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-24 bg-[#4A4C7D] rounded-md" />
            <Skeleton className="h-8 w-8 rounded-full bg-[#4A4C7D]" />
          </div>
        ) : user && profile && profile.firstName ? (
          // Display greeting, notification bell, and avatar when loaded (check firstName to ensure profile has data)
          <>
            <span className="text-sm sm:text-lg text-white whitespace-nowrap">
              Hi, {profile.firstName}
            </span>
            {/* Theme toggle hidden — dark mode soft-disabled (2026-02).
                Re-enable by un-commenting this and reverting the pin in
                /app/src/utils/ThemeProvider.tsx. */}
            {/* <ThemeToggleMenu className="text-white hover:bg-[#4A4C7D]" /> */}
            <NotificationBell />
            <Avatar className="h-10 w-10 sm:h-12 sm:w-12">
              <AvatarImage src={profile.photoURL} alt={`${profile.firstName} ${profile.lastName || ''}`} />
              <AvatarFallback className="bg-[#4A4C7D] text-white">
                {/* Simple initials fallback */}
                {profile.firstName?.charAt(0).toUpperCase()}{profile.lastName?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </>
        ) : user ? (
          // Fallback if user logged in but profile hasn't loaded or doesn't exist yet
           <div className="flex items-center gap-2">
             <Skeleton className="h-8 w-24 bg-[#4A4C7D] rounded-md" />
            <Skeleton className="h-8 w-8 rounded-full bg-[#4A4C7D]" />
           </div>
        ) : (
          // Placeholder when not logged in (or adjust as needed)
          <div className="h-8"></div>
        )}
      </div>

      {/* Conditional Role-Based Links */}
      {showLoggedInControls && profile && (
          <div className="flex items-center gap-2 ml-2"> {/* Use ml-2 or adjust spacing as needed */}
             {/* Add links visible to multiple roles or all logged-in users here if needed */}
          </div>
      )}

      {/* Login/Logout Button - larger margin on lg screens to avoid chat sidebar overlap */}
      <div className="flex-shrink-0 mr-2 lg:mr-[340px]">
        {loading ? (
          // Show a skeleton matching button size while loading
          <Skeleton className="h-10 w-20 sm:h-12 sm:w-26 bg-[#4A4C7D] rounded-md" />
        ) : user ? (
          // Show Logout button if user is logged in
          <Button
            variant="destructive"
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white px-2 sm:px-4 text-sm sm:text-base h-9 sm:h-10"
          >
            Logout
          </Button>
        ) : (
          // Show Login button if user is not logged in
          <Button
            onClick={handleLogin}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-2 sm:px-4 text-sm sm:text-base h-9 sm:h-10"
          >
            Login
          </Button>
        )}
      </div>

      {/* Act as User Dialog */}
      <ActAsUserDialog 
        open={actAsUserDialogOpen} 
        onOpenChange={setActAsUserDialogOpen} 
      />
    </div>
  );
}
